namespace $.$$ {
	type $shm_hitalama_map_book_widgets3_model = {
		roots: readonly string[]
		children: ReadonlyMap< string, readonly string[] >
	}

	export class $shm_hitalama_map_book_widgets3 extends $.$shm_hitalama_map_book_widgets3 {

		_drag_start_pos = new Map< string, readonly [ number, number ] >
		_drag_moved = new Set< string >

		@ $mol_action
		close_widgets_list() {
			this.widget_list_visible( false )
		}

		link_from_id( id: any ) {
			return new $giper_baza_link( id )
		}

		ref_str( id: $giper_baza_link ) {
			return id.toString()!
		}

		Tree() {
			const obj = super.Tree()
			const patched = obj as typeof obj & { _hitalama_widgets3_patched?: true }
			if( patched._hitalama_widgets3_patched ) return obj

			const on_drag_start = obj.on_drag_start.bind( obj )
			obj.on_drag_start = ( id: any, event: PointerEvent )=> {
				this.widget_drag_start( id, event )
				return on_drag_start( id, event )
			}

			const on_drag_end = obj.on_drag_end.bind( obj )
			obj.on_drag_end = ( id: any, event?: PointerEvent )=> {
				if( !this.widget_drag_real( id, event ) ) {
					const result = on_drag_end( id, event )
					this.virtual_active( false )
					this.widget_activate( id, event )
					return result
				}

				const result = on_drag_end( id, event )
				this.widget_drag_end( id, event )
				return result
			}

			patched._hitalama_widgets3_patched = true
			return obj
		}

		@ $mol_mem
		widgets() {
			const widgets = this.map().Widgets_list()?.remote_list() ?? []
			return widgets.filter( widget => {
				if( widget.removed() ) return false
				if( !this.map().viewport_frames_visible() && widget.type() == 'viewport_frame' ) return false
				if( widget.container_widget()?.type() == 'notebook' ) return false
				return true
			} )
		}

		filter_normalized() {
			return this.filter().trim().toLowerCase()
		}

		widget_matches_filter( widget: $shm_hitalama_map_widget ) {
			const filter = this.filter_normalized()
			if( !filter ) return true
			return [
				widget.title(),
				widget.type(),
				widget.link_str(),
				... widget.tags(),
			].some( value => String( value ).toLowerCase().includes( filter ) )
		}

		@ $mol_mem
		widgets_filtered() {
			const filter = this.filter_normalized()
			const widgets = this.widgets()
			if( !filter ) return widgets

			const visible = new Set< string >
			for( const widget of widgets ) {
				if( !this.widget_matches_filter( widget ) ) continue
				let current: $shm_hitalama_map_widget | null | undefined = widget
				while( current ) {
					visible.add( current.link_str() )
					current = current.container_widget()
				}
			}

			return widgets.filter( widget => visible.has( widget.link_str() ) )
		}

		widgets_by_id() {
			return new Map( this.widgets_filtered().map( widget => [ widget.link_str(), widget ] ) )
		}

		widget_add( type: string ) {
			const widget = ( this.Add_widget_menu().presets() as any )[ type ]( [ 0, 0 ] ) as $shm_hitalama_map_widget
			this.$.$mol_state_arg.value( 'edit_widget', widget.link().str )!
			this.Head().Settings().showed( false )
			return widget
		}

		@ $mol_mem_key
		Widget_item( id: any ): $mol_view {
			return this.editor_mode_enabled()
				? this.Editor_widget_item( id )
				: super.Widget_item( id )
		}

		removed_widgets() {
			const widgets = this.map().Widgets_removed_list()?.remote_list() ?? []
			return widgets.filter( widget => this.widget_matches_filter( widget ) )
		}

		removed_widget_rows() {
			return this.removed_widgets().map( widget => this.Removed_widget_item( widget.link_str() ) )
		}

		removed_widgets_visible() {
			return this.show_removed() ? super.removed_widgets_visible() : []
		}

		widget_by_id( id: any ) {
			return this.map().widget( id )
		}

		@ $mol_mem
		model_tree(): $shm_hitalama_map_book_widgets3_model {
			const widgets = this.widgets_filtered()
			const children = new Map< string, readonly string[] >
			const roots: string[] = []
			const visited = new Set< string >

			const add = ( widget: $shm_hitalama_map_widget )=> {
				const id = widget.link_str()
				if( visited.has( id ) ) return
				visited.add( id )
				children.set( id, this.inner_widgets( widget ).map( child => child.link_str() ) )
				for( const child of this.inner_widgets( widget ) ) add( child )
			}

			for( const widget of widgets ) {
				if( widget.container_widget() ) continue
				roots.push( widget.link_str() )
				add( widget )
			}

			for( const widget of widgets ) add( widget )
			return { roots: roots.toReversed(), children }
		}

		@ $mol_mem_key
		inner_widgets( widget: $shm_hitalama_map_widget ) {
			const widgets = this.widgets_filtered()
			const ids = new Set( widgets.map( child => child.link_str() ) )
			const children = widget.widgets().filter( child => ids.has( child.link_str() ) )
			const order = new Map( widgets.map( ( child, index )=> [ child.link_str(), index ] ) )
			const sorted = children
				.toSorted( ( left, right )=> ( order.get( left.link_str() ) ?? 0 ) - ( order.get( right.link_str() ) ?? 0 ) )
			return this.children_reversed( widget ) ? sorted.toReversed() : sorted
		}

		children_reversed( widget: $shm_hitalama_map_widget ) {
			return widget.type() == 'group'
		}

		needs_children_layout( widget: $shm_hitalama_map_widget ) {
			return [ 'layout_vert', 'layout_hor', 'notebook2' ].includes( widget.type() )
		}

		@ $mol_mem
		virtual_active( next?: boolean ) {
			return next ?? false
		}

		@ $mol_mem
		virtual_roots( next?: readonly string[] ) {
			return next ?? this.model_tree().roots
		}

		@ $mol_mem_key
		virtual_children( id: any, next?: readonly string[] ) {
			return next ?? this.model_tree().children.get( id ) ?? []
		}

		@ $mol_action
		virtual_reset() {
			const model = this.model_tree()
			this.virtual_roots( [ ...model.roots ] )
			for( const [ id, children ] of model.children ) this.virtual_children( id, [ ...children ] )
			this.virtual_active( true )
		}

		@ $mol_mem
		tree_roots( next?: readonly string[] ) {
			if( next !== undefined ) {
				this.virtual_roots( [ ...next ] )
				return next
			}
			return this.virtual_active() ? this.virtual_roots() : this.model_tree().roots
		}

		@ $mol_mem_key
		tree_children( id: any, next?: readonly string[] ) {
			if( next !== undefined ) {
				this.virtual_children( id, [ ...next ] )
				return next
			}
			return this.virtual_active() ? this.virtual_children( id ) : this.model_tree().children.get( id ) ?? []
		}

		@ $mol_action
		widget_drag_start( id: any, event?: PointerEvent ) {
			this._drag_moved.delete( id )
			if( event ) this._drag_start_pos.set( id, [ event.clientX, event.clientY ] )
			if( !this.virtual_active() ) this.virtual_reset()
			return event ?? null
		}

		widget_drag_real( id: any, event?: PointerEvent ) {
			const start = this._drag_start_pos.get( id )
			if( !start || !event ) return false

			const dx = event.clientX - start[0]
			const dy = event.clientY - start[1]
			const moved = dx * dx + dy * dy > 16
			if( moved ) this._drag_moved.add( id )
			return moved
		}

		@ $mol_action
		widget_drag_end( id: any, event?: PointerEvent ) {
			this.commit_virtual_tree()
			this.virtual_active( false )
			new this.$.$mol_after_timeout( 0, ()=> this._drag_moved.delete( id ) )
			return event ?? null
		}

		can_contain( widget: $shm_hitalama_map_widget ) {
			return [ 'group', 'layout_vert', 'layout_hor', 'notebook', 'notebook2' ].includes( widget.type() )
		}

		can_have_children( id: any ) {
			return this.can_contain( this.widget_by_id( id ) )
		}

		@ $mol_action
		commit_virtual_tree() {
			const widgets = this.widgets()
			const widget_ids = new Set( widgets.map( widget => widget.link_str() ) )
			const raw_children = new Map< string, string[] >( widgets.map( widget => [
				widget.link_str(),
				( this.virtual_children( widget.link_str() ) as string[] ).filter( id => widget_ids.has( id ) ),
			] ) )
			const parent_by_id = new Map< string, string | null >
			const children = new Map< string, string[] >( widgets.map( ( widget: $shm_hitalama_map_widget ) => [ widget.link_str(), [] ] ) )
			const roots: string[] = []
			const visited = new Set< string >

			const add = ( id: string, parent: string | null )=> {
				if( visited.has( id ) ) return
				if( !widget_ids.has( id ) ) return

				visited.add( id )
				parent_by_id.set( id, parent )
				if( parent == null ) roots.push( id )
				else children.get( parent )?.push( id )

				const child_ids = raw_children.get( id ) ?? []
				for( const child of child_ids ) add( child, id )
			}

			for( const id of this.virtual_roots() ) add( id, null )
			for( const widget of widgets ) add( widget.link_str(), null )

			for( const widget of widgets ) {
				const id = widget.link_str()
				const parent_id = parent_by_id.get( id ) ?? null
				const parent_next = parent_id ? this.widget_by_id( parent_id ) : null
				const parent_prev = widget.container_widget()
				if( parent_prev?.link_str() === parent_next?.link_str() ) continue

				this.map().widget_detach( widget.link() )
				widget.container_widget( parent_next )
				if( parent_next ) parent_next.Widgets( null )?.add( widget.link() )
				else this.shift_detached_widget( widget, parent_prev )
			}

			for( const widget of widgets ) {
				const ids = children.get( widget.link_str() ) ?? []
				const store_ids = this.children_reversed( widget ) ? ids.toReversed() : ids
				const next = store_ids.map( id => this.widget_by_id( id ) )
				widget.Widgets( null )?.remote_list( next as any )
				this.arrange_children( widget, store_ids )
			}

			this.commit_z_order( roots, children )
		}

		@ $mol_action
		arrange_children( widget: $shm_hitalama_map_widget, ids: readonly string[] ) {
			if( !this.needs_children_layout( widget ) ) return

			const view = this.Map_page().Widget( widget.link() ).Sub() as $shm_hitalama_map_widget_float_layout_vert | $shm_hitalama_map_widget_float_layout_hor
			view.move_widgets_to_places( [ ...ids ] )
		}

		@ $mol_action
		shift_detached_widget( widget: $shm_hitalama_map_widget, container: $shm_hitalama_map_widget | null | undefined ) {
			if( !container ) return
			if( container.type() != 'notebook2' ) return

			const container_right = container.body_x() + container.right_edge_x()
			const left_edge = widget.Left_edge_x()?.val() ?? 0
			widget.body_x( container_right + 20 - left_edge )
		}

		@ $mol_action
		commit_z_order( roots: readonly string[], children: ReadonlyMap< string, readonly string[] > ) {
			const ids: string[] = []
			const add = ( id: string )=> {
				ids.push( id )
				const child_ids = children.get( id ) ?? []
				const widget = this.widget_by_id( id )
				const store_ids = this.children_reversed( widget ) ? child_ids.toReversed() : child_ids
				for( const child of store_ids ) add( child )
			}
			for( const id of roots.toReversed() ) add( id )

			const list = this.map().Widgets_list( null )
			const widgets = ( list?.remote_list() ?? [] ) as $shm_hitalama_map_widget[]
			const by_id = new Map( widgets.map( widget => [ widget.link_str(), widget ] ) )
			const ordered = [
				... ids.flatMap( id => by_id.get( id ) ? [ by_id.get( id )! ] : [] ),
				... widgets.filter( widget => !ids.includes( widget.link_str() ) ),
			]
			list?.remote_list( ordered as any )
		}

		@ $mol_mem_key
		widget_title( id: any, next?: string ) {
			return this.widget_by_id( id ).title( next )
		}

		widget_type( id: any ) {
			return this.widget_by_id( id ).type()
		}

		@ $mol_mem_key
		widget_title_draft( id: any, next?: string ): string {
			return next ?? this.widget_title( id )
		}

		@ $mol_mem_key
		widget_rename( id: any, next?: boolean ) {
			return next ?? false
		}

		@ $mol_mem_key
		widget_title_content( id: any ) {
			return this.widget_rename( id )
				? [ this.Widget_title_input( id ) ]
				: [ this.Widget_title_button( id ) ]
		}

		@ $mol_action
		widget_rename_start( id: any, event?: Event | null ) {
			event?.preventDefault()
			event?.stopPropagation()
			this.widget_title_draft( id, this.widget_title( id ) )
			this.widget_rename( id, true )
			new this.$.$mol_after_frame( ()=> this.widget_rename_focus( id ) )
			return event ?? null
		}

		@ $mol_action
		widget_rename_focus( id: any ) {
			const input = this.Widget_title_input( id )
			input.focused( true )
			input.selection([ 0, this.widget_title_draft( id ).length ])
		}

		@ $mol_action
		widget_rename_pointerdown( id: any, event?: PointerEvent | null ) {
			event?.stopPropagation()
			return event ?? null
		}

		@ $mol_action
		widget_rename_submit( id: any, event?: Event | null ) {
			this.widget_title( id, this.widget_title_draft( id ) )
			this.widget_rename( id, false )
			return event ?? null
		}

		@ $mol_action
		widget_rename_keydown( id: any, event?: KeyboardEvent | null ) {
			if( event?.key !== 'Escape' ) return event ?? null

			event.preventDefault()
			event.stopPropagation()
			this.widget_title_draft( id, this.widget_title( id ) )
			this.widget_rename( id, false )
			return event
		}

		@ $mol_mem_key
		Widget_icon( id: any ): $mol_icon_square_outline {
			const widget = this.widget_by_id( id )
			const Icon = $shm_hitalama_map_widget_def( widget.type() )?.icon
			if( !Icon ) return super.Widget_icon( id )
			return new Icon() as $mol_icon_square_outline
		}

		@ $mol_mem_key
		edit_widget_uri( id: any ): string {
			const dict = this.$.$mol_state_arg.dict()
			return this.$.$mol_state_arg.link({ ...dict, edit_widget: this.ref_str( this.link_from_id( id ) ) })
		}

		@ $mol_mem_key
		widget_selected( id: any ): boolean {
			return this.Map_page().selected( this.link_from_id( id ) )
		}

		@ $mol_action
		widget_restore( id: any, event?: Event | null ) {
			event?.preventDefault()
			event?.stopPropagation()
			this.map().widget_restore( this.link_from_id( id ) )
			return event ?? null
		}

		@ $mol_action
		widget_activate( id: any, event?: Event | null ) {
			return this.editor_mode_enabled()
				? this.widget_edit( id, event )
				: this.widget_click( id, event )
		}

		@ $mol_action
		widget_edit( id: any, event?: Event | null ) {
			if( this._drag_moved.has( id ) ) {
				event?.preventDefault()
				event?.stopPropagation()
				return event ?? null
			}

			event?.preventDefault()
			event?.stopPropagation()
			this.$.$mol_state_arg.value( 'edit_widget', this.ref_str( this.link_from_id( id ) ) )
			return event ?? null
		}

		@ $mol_action
		widget_link_dragstart( id: any, event?: DragEvent | null ) {
			event?.preventDefault()
			return event ?? null
		}

		@ $mol_action
		widget_click( id: any, event?: Event | null ) {
			if( this._drag_moved.has( id ) ) {
				event?.preventDefault()
				event?.stopPropagation()
				return event ?? null
			}

			if( this.widget_click_doubled( event ) ) return this.widget_rename_start( id, event )

			event?.preventDefault()
			event?.stopPropagation()
			const widget = this.widget_by_id( id )
			const target = widget.container_widget()?.type() == 'notebook'
				? widget.container_widget()!.link()
				: this.link_from_id( id )

			this.go_to_widget( target )
			this.Map_page().selected( this.link_from_id( id ), true )
			return event ?? null
		}

		widget_click_doubled( event?: Event | null ) {
			const pointer = event as MouseEvent | PointerEvent | null | undefined
			return ( pointer?.detail ?? 0 ) >= 2
		}

	}
}
